import { Routes, Route, Navigate, useLocation, useSearchParams, Outlet } from "react-router-dom";
import { AdminLayout } from "./shared/layout";
import { DeviceGuard } from "./shared/components";
import { DashboardPage } from "./pages/DashboardPage";
import { ProductsPage } from "./pages/ProductsPage";
import { AddProductPage } from "./pages/AddProductPage/index";
import { EditProductPage } from "./pages/EditProductPage/index";
import { OrdersPage } from "./pages/OrdersPage";
import { CustomOrdersPage } from "./features/custom-orders/pages/CustomOrdersPage";
import { NoAccessPage } from "./pages/NoAccessPage";
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import NavPage from "./pages/NavPage";
import { ProductMetadataPage } from "./pages/ProductMetadataPage/index";
import { HomepageSliderPage } from "./pages/HomepageSliderPage/index";
import { MerchantsPage } from "./pages/MerchantsPage";
import { MerchantDetailPage } from "./pages/MerchantDetailPage";
import { TenantsPage } from "./pages/TenantsPage";
import { TenantDetailPage } from "./pages/TenantDetailPage";
import { StorefrontReadinessPage } from "./pages/StorefrontReadinessPage";
import { InvoicesPage } from "./pages/InvoicesPage";
import { InvoiceDetailPage } from "./pages/InvoiceDetailPage";
import { DisputesPage } from "./pages/DisputesPage";
import { AuditLogsPage } from "./pages/AuditLogsPage";
import { ReconciliationPage } from "./pages/ReconciliationPage";
import { PaymentsPage } from "./pages/PaymentsPage";
import { TcsConfigPage } from "./features/products/TcsConfigPage";
import { SolutionCatalogPage } from "./features/field-config/SolutionCatalogPage";
import StaffPage from "./features/staff/index";
import LeadsPage from "./features/leads/index";
import Payouts from "./features/payouts";
import { BookingServicesPage } from "./features/booking-services/pages/BookingServicesPage";
import { CharterServicesPage } from "./features/charter/pages/CharterServicesPage";
import { CharterCataloguePage } from "./features/charter/pages/CharterCataloguePage";
import { CharterBookingsPage } from "./features/charter/pages/CharterBookingsPage";
import { CharterOrdersPage } from "./features/charter/pages/CharterOrdersPage";
import { CharterSettingsPage } from "./features/charter/pages/CharterSettingsPage";
import { CharterWaitlistPage } from "./features/charter/pages/CharterWaitlistPage";
import { CharterPortfolioPage } from "./features/charter/pages/CharterPortfolioPage";
import { CharterProvidersPage } from "./features/charter/pages/CharterProvidersPage";
import { CharterProviderDetailPage } from "./features/charter/pages/CharterProviderDetailPage";
import { CharterOversightPage } from "./features/charter/pages/CharterOversightPage";
import MerchantAdd from "./features/merchants/MerchantAdd";
import { ResetPasswordPage } from "./pages/onboarding/ResetPasswordPage";
import { OnboardingPage } from "./pages/onboarding/OnboardingPage";
import { TenantGuard } from "./shared/components/TenantGuard";
import { MerchantSelectorModal } from "./shared/components/MerchantSelectorModal";
import { useAuthStore } from "./features/auth/store";
import { ToastProvider, ToastContainer } from "./shared/components/toast";
import { ProtectedRoute } from "./shared/components/ProtectedRoute";
import { Permission, getDefaultRouteForRole } from "./features/auth/permissions";

const WithAdminLayout = () => (
	<AdminLayout><Outlet /></AdminLayout>
);

function App() {
	const { isAuthenticated, user } = useAuthStore();
	const location = useLocation();
	const [searchParams] = useSearchParams();

	// Redirect authenticated users away from /login before AdminLayout mounts,
	// so the sidebar never flashes over a blank content area.
	if (isAuthenticated && location.pathname === '/login') {
		return <Navigate to={searchParams.get('redirect_to') ?? '/'} replace />;
	}

	return (
		<ToastProvider>
			<DeviceGuard>
				<Routes>
					{/* Public routes — always accessible regardless of auth state */}
					<Route path="/onboard/claim" element={<ResetPasswordPage />} />
					<Route path="/forgot-password" element={<ForgotPasswordPage />} />
					<Route path="/nav" element={<NavPage />} />

					{isAuthenticated ? (
						<Route element={<WithAdminLayout />}>
							<Route path="/login" element={<Navigate to={getDefaultRouteForRole(user?.roleName)} replace />} />
							<Route path="/" element={<ProtectedRoute permission={Permission.DASHBOARD_VIEW}><DashboardPage /></ProtectedRoute>} />
							<Route path="/products" element={<ProtectedRoute permissions={[Permission.PRODUCT_READ_ALL, Permission.PRODUCT_READ_PUBLISHED]}><ProductsPage /></ProtectedRoute>} />
							<Route path="/products/add" element={<ProtectedRoute permission={Permission.PRODUCT_CREATE} fallback="/products"><AddProductPage /></ProtectedRoute>} />
							<Route path="/products/edit/:id" element={<ProtectedRoute permission={Permission.PRODUCT_UPDATE} fallback="/products"><EditProductPage /></ProtectedRoute>} />
							<Route path="/orders" element={<ProtectedRoute permission={Permission.ORDER_READ}><OrdersPage /></ProtectedRoute>} />
							<Route path="/custom-orders" element={<ProtectedRoute permission={Permission.ORDER_READ}><CustomOrdersPage /></ProtectedRoute>} />
							<Route path="/tenants" element={<ProtectedRoute permission={Permission.TENANT_VIEW}><TenantsPage /></ProtectedRoute>} />
							<Route path="/tenants/:id" element={<ProtectedRoute permission={Permission.TENANT_VIEW}><TenantDetailPage /></ProtectedRoute>} />
							<Route path="/merchants" element={<ProtectedRoute permission={Permission.MERCHANT_VIEW}><MerchantsPage /></ProtectedRoute>} />
							<Route path="/storefronts" element={<ProtectedRoute permission={Permission.MERCHANT_VIEW}><StorefrontReadinessPage /></ProtectedRoute>} />
							<Route path="/merchants/add" element={<ProtectedRoute permission={Permission.MERCHANT_CREATE} fallback="/no-access"><MerchantAdd /></ProtectedRoute>} />
							<Route path="/merchants/:id" element={<ProtectedRoute permission={Permission.MERCHANT_VIEW}><MerchantDetailPage /></ProtectedRoute>} />
							<Route path="/invoices" element={<ProtectedRoute permission={Permission.INVOICE_VIEW}><InvoicesPage /></ProtectedRoute>} />
							<Route path="/invoices/:id" element={<ProtectedRoute permission={Permission.INVOICE_VIEW}><InvoiceDetailPage /></ProtectedRoute>} />
							<Route path="/payments" element={<ProtectedRoute permission={Permission.PAYMENT_VIEW}><PaymentsPage /></ProtectedRoute>} />
							<Route path="/payouts" element={<ProtectedRoute permission={Permission.SETTLEMENT_VIEW}><Payouts /></ProtectedRoute>} />
							<Route path="/disputes" element={<ProtectedRoute permission={Permission.PAYMENT_DISPUTE_VIEW}><DisputesPage /></ProtectedRoute>} />
							<Route path="/reconciliation" element={<ProtectedRoute permission={Permission.RECONCILIATION_VIEW}><ReconciliationPage /></ProtectedRoute>} />
							<Route path="/attribute-schemas" element={<ProtectedRoute permission={Permission.PRODUCT_SCHEMA_MANAGE}><TcsConfigPage /></ProtectedRoute>} />
							<Route path="/intake-catalog" element={<ProtectedRoute permission={Permission.PRODUCT_SCHEMA_MANAGE}><SolutionCatalogPage /></ProtectedRoute>} />
							<Route path="/no-access" element={<NoAccessPage />} />
							<Route path="/product-metadata" element={<ProtectedRoute permission={Permission.METADATA_VIEW}><ProductMetadataPage /></ProtectedRoute>} />
							<Route path="/homepage-slider" element={<ProtectedRoute permission={Permission.SLIDER_VIEW}><HomepageSliderPage /></ProtectedRoute>} />
							<Route path="/booking-services" element={<ProtectedRoute permission={Permission.BOOKING_SERVICE_VIEW}><BookingServicesPage /></ProtectedRoute>} />
							<Route path="/staff" element={<ProtectedRoute permission={Permission.STAFF_VIEW}><StaffPage /></ProtectedRoute>} />
							<Route path="/leads" element={<ProtectedRoute permission={Permission.LEADS_VIEW}><LeadsPage /></ProtectedRoute>} />
							<Route path="/audit-logs" element={<ProtectedRoute permission={Permission.AUDIT_LOG_VIEW}><AuditLogsPage /></ProtectedRoute>} />
							{/* Charter (PE-1831) — merchant back-office (provider-scoped via selected merchant) */}
							<Route path="/charter/services" element={<ProtectedRoute permission={Permission.CHARTER_VIEW}><CharterServicesPage /></ProtectedRoute>} />
							<Route path="/charter/catalogue" element={<ProtectedRoute permission={Permission.CHARTER_VIEW}><CharterCataloguePage /></ProtectedRoute>} />
							<Route path="/charter/bookings" element={<ProtectedRoute permission={Permission.CHARTER_VIEW}><CharterBookingsPage /></ProtectedRoute>} />
							<Route path="/charter/orders" element={<ProtectedRoute permission={Permission.CHARTER_VIEW}><CharterOrdersPage /></ProtectedRoute>} />
							<Route path="/charter/portfolio" element={<ProtectedRoute permission={Permission.CHARTER_VIEW}><CharterPortfolioPage /></ProtectedRoute>} />
							<Route path="/charter/settings" element={<ProtectedRoute permission={Permission.CHARTER_VIEW}><CharterSettingsPage /></ProtectedRoute>} />
							<Route path="/charter/waitlist" element={<ProtectedRoute permission={Permission.CHARTER_VIEW}><CharterWaitlistPage /></ProtectedRoute>} />
							{/* Charter — TCS-admin cross-provider oversight */}
							<Route path="/charter/providers" element={<ProtectedRoute permission={Permission.CHARTER_ADMIN}><CharterProvidersPage /></ProtectedRoute>} />
							<Route path="/charter/providers/:merchantId" element={<ProtectedRoute permission={Permission.CHARTER_ADMIN}><CharterProviderDetailPage /></ProtectedRoute>} />
							<Route path="/charter/oversight" element={<ProtectedRoute permission={Permission.CHARTER_ADMIN}><CharterOversightPage /></ProtectedRoute>} />
							{/* Onboarding — overlay on top of AdminLayout */}
							<Route path="/onboarding/reset-password" element={<ResetPasswordPage />} />
							<Route path="/onboarding/setup" element={<OnboardingPage />} />
							<Route path="*" element={<Navigate to={getDefaultRouteForRole(user?.roleName)} replace />} />
						</Route>
					) : (
						<>
							<Route path="/login" element={<LoginPage />} />
							<Route path="*" element={<Navigate to="/login" replace />} />
						</>
					)}
				</Routes>
			</DeviceGuard>
			<ToastContainer />
			{isAuthenticated && <TenantGuard />}
			{isAuthenticated && <MerchantSelectorModal />}
		</ToastProvider>
	);
}

export default App;
